<template>
  <div class="container py-4">
    <div class="row justify-content-center">
      <!-- Gate: host/cohost access -->
      <div class="col-md-8" v-if="eventAcess === 'yes'">
        <!-- Top bar -->
        <div class="page-bar py-3">
          <div class="container">
            <div class="d-flex align-items-center">
              <BackButton class="btn btn-link p-0 mr-2" fallback="/host">
                <i class="fas fa-chevron-left"></i>
              </BackButton>
              <div class="page-title flex-grow-1">{{ eventTitle || 'Event' }}</div>

              <div class="dropdown">
                <button class="dot-btn btn p-1 action-menu-toggle" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                  <i class="fas fa-ellipsis-v"></i>
                </button>
                <div class="dropdown-menu dropdown-menu-right action-menu">
                  <router-link
                    class="dropdown-item action-menu__item"
                    v-if="eventType == 'new' && cohostAddStatus == '1'"
                    :to="`/cohost/${eventId}`"
                  >
                    <i class="far fa-edit mr-2"></i><span>Add CoHost</span>
                  </router-link>

                  <router-link class="dropdown-item action-menu__item" v-if="eventEdit == 'yes'" :to="`/edit-invitation/${eventId}`">
                    <i class="far fa-copy mr-2"></i><span>Edit Invitation</span>
                  </router-link>

                  <router-link class="dropdown-item action-menu__item" v-if="eventEdit == 'yes'" :to="`/event-settings/${eventId}`">
                    <i class="fas fa-sliders-h mr-2"></i><span>Edit Event Settings</span>
                  </router-link>

                  <!-- Share via & QR -->
                  <a class="dropdown-item action-menu__item" href="#" @click.prevent="showShareVia = true">
                    <i class="far fa-share-square mr-2"></i><span>Share Via</span>
                  </a>
                  <a class="dropdown-item action-menu__item" href="#" @click.prevent="showQrModal = true">
                    <i class="fas fa-qrcode mr-2"></i><span>Album QR Code</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Main content -->
        <div class="container mt-3 mb-5 shadow-sm bg-white py-2 rounded" v-if="loaded">
          <!-- Carousel -->
          <div class="hero my-3" v-if="slidesClean.length">
            <div class="slide-frame" @mouseenter="pauseAuto" @mouseleave="resumeAuto">
              <div
                v-if="useContain"
                class="slide-bg-blur"
                :style="{ backgroundImage: `url(${slidesClean[currentSlide]})` }"
              ></div>

              <img
                :key="`slide-${currentSlide}`"
                :src="slidesClean[currentSlide]"
                class="slide-img"
                :class="useContain ? 'fit-contain' : 'fit-cover'"
                :alt="`Slide ${currentSlide + 1}`"
                @load="onImgLoad"
              />

              <button v-if="slidesClean.length > 1" class="nav prev" @click="prevSlide" aria-label="Previous">
                <i class="fas fa-chevron-left"></i>
              </button>
              <button v-if="slidesClean.length > 1" class="nav next" @click="nextSlide" aria-label="Next">
                <i class="fas fa-chevron-right"></i>
              </button>
            </div>

            <div class="thumbs mt-2" v-if="slidesClean.length > 1">
              <button
                v-for="(src, i) in slidesClean"
                :key="`thumb-${i}-${src}`"
                class="thumb-btn"
                :class="{ active: i === currentSlide }"
                @click="goToSlide(i)"
                :title="`Go to image ${i+1}`"
              >
                <img v-no-right-click :src="src" class="thumb" alt="thumbnail" />
              </button>
            </div>
          </div> 
          
          <!-- Title + Host -->
          <h5 class="mb-1">{{ eventTitle || 'Event' }}</h5>
          <div class="mb-3 text-small">
            Host: <strong>{{ hostName || '-' }}</strong>
          </div>

          <!-- Event details -->
          <div class="mb-3">
            <div class="section-title">Event Details</div>

            <div class="icon-row d-flex align-items-start text-small mb-2">
              <span class="emoji-icon mt-1 mr-2" aria-hidden="true">📅</span>
              <div>{{ eventDateRange }}</div>
            </div>

            <div class="icon-row d-flex align-items-start text-small mb-2">
              <span class="emoji-icon mt-1 mr-2" aria-hidden="true">📍</span>
              <div class="flex-grow-1 text-dark">
                <template v-if="eventPlace">{{ eventPlace }}</template>
                <template v-if="doorNo">, {{ doorNo }}</template>
                <template v-if="Address">, {{ Address }}</template>
              </div>
              <a
                v-if="lat && lng"
                :href="mapHref"
                target="_blank"
                rel="noopener"
                class="ml-2 link-arrow"
                title="Open in Maps"
              >
                <i class="fas fa-location-arrow"></i>
              </a>
            </div>

            <div v-if="hasMap" class="map-card mt-3">
              <div class="map-card__header">
                <span><i class="fas fa-map-marked-alt mr-2"></i>Event Location</span>
                <a :href="mapHref" target="_blank" rel="noopener">Open in Google Maps</a>
              </div>
              <div class="map-frame">
                <iframe
                  class="map-frame__iframe"
                  :src="mapEmbedSrc"
                  loading="lazy"
                  allowfullscreen
                  referrerpolicy="no-referrer-when-downgrade"
                  aria-label="Event location map"
                ></iframe>
              </div>
            </div>
          </div>

          <!-- About -->
          <div class="mb-3" v-if="description">
            <div class="section-title">About The Event</div>
            <p class="mb-2">{{ description }}</p>
          </div>

          <!-- Invitation Count -->
          <div class="mb-3">
            <div class="section-title">Invitation Count</div>
            <div class="count-bar">
              <div class="count-pill count-total">
                <div class="num">{{ totals.total }}</div>
                <div class="lbl">Total</div>
              </div>
              <div class="count-pill count-sent">
                <div class="num">{{ totals.sent }}</div>
                <div class="lbl">Sent</div>
              </div>
              <div class="count-pill count-pending">
                <div class="num">{{ totals.pending }}</div>
                <div class="lbl">Pending</div>
              </div>
            </div>
          </div>

          <!-- RSVP Status -->
          <div class="mb-3" v-if="rsvpOption == 1">
            <div class="section-title">RSVP Status</div>
            <div class="rsvp-bar">
              <div class="rsvp-seg rsvp-yes"><span class="num">{{ rsvp.yes }}</span>Yes</div>
              <div class="rsvp-seg rsvp-maybe"><span class="num">{{ rsvp.maybe }}</span>Maybe</div>
              <div class="rsvp-seg rsvp-no"><span class="num">{{ rsvp.no }}</span>No</div>
              <div class="rsvp-seg rsvp-nr"><span class="num">{{ rsvp.noresponse }}</span>No Response</div>
            </div>
          </div>

          <!-- Attendance -->
          <div class="mb-4" v-if="rsvpOption == 1 && rsvpFamily == 1">
            <div class="section-title">How many are coming?</div>
            <div class="row">
              <div class="col-md-6 mb-3">
                <div class="att-card">
                  <div class="att-head confirmed d-flex align-items-center">Confirmed</div>
                  <div class="att-body">
                    <div class="row">
                      <div class="col-6">
                        <div class="att-box">
                          <div class="h3">{{ attendance.yesAdults }}</div>
                          <div class="small">Adults</div>
                        </div>
                      </div>
                      <div class="col-6">
                        <div class="att-box">
                          <div class="h3">{{ attendance.yesChildren }}</div>
                          <div class="small">Children</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-6 mb-3">
                <div class="att-card">
                  <div class="att-head maybe d-flex align-items-center">Maybe</div>
                  <div class="att-body">
                    <div class="row">
                      <div class="col-6">
                        <div class="att-box">
                          <div class="h3">{{ attendance.maybeAdults }}</div>
                          <div class="small">Adults</div>
                        </div>
                      </div>
                      <div class="col-6">
                        <div class="att-box">
                          <div class="h3">{{ attendance.maybeChildren }}</div>
                          <div class="small">Children</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <hr />

          <!-- Vendors -->
          <div class="mb-4">
            <div class="section-title">Vendors</div>
            <p class="text-small text-muted mb-2">
              Find and connect with top vendors for your event. Send enquiries to discover the best services that suit your needs.
            </p>
            <div class="d-flex">
              <router-link :to="`/detail/enquiry-list/${eventId}`" class="vendor-pill">
                Enquiries ({{ eventSendEnquiryCount }})
              </router-link>
              <router-link :to="`/detail/vendors/${eventId}`" class="vendor-pill">
                Hired Vendors
              </router-link>
            </div>
          </div>

          <div class="mb-4 post-card">
            <div class="post-card__content">
              <div class="post-card__title">Post to this event</div>
              <div class="post-card__sub">Share important information or fun moments from the event.</div>
            </div>
            <i class="fas fa-chevron-right post-card__icon" aria-hidden="true"></i>
          </div>

          <hr />
 
          <!-- Guests --> 
          <div class="mb-3">
            <div class="badge-tabs" role="tablist" aria-label="Guest tabs">
              <button
                v-for="tab in guestTabs"
                :key="tab.key"
                type="button"
                class="badge-tab"
                :class="{ active: tab.key === activeGuestsTab }"
                @click="setActiveGuestsTab(tab.key)"
              >
                {{ tab.label }}
              </button>
            </div>
          </div>

          <GuestsNotesList
            v-if="isGuestsNotesTab"
            :notes="guestNotes"
            :loading="guestNotesLoading"
          />
          
          <GuestsList
            v-else
            :members="members"
            :event-id="eventId"
            :session-user-id="userId"
            :event-type="eventType"
            :rsvp-option="rsvpOption"
            :rsvp-family="rsvpFamily"
            @changed="loadInvitation"
          />
        </div>

        <!-- Loading -->
        <div v-else class="text-center p-5">
          <div class="spinner-border text-primary" role="status">
            <span class="sr-only">Loading...</span>
          </div>
          <div class="mt-2 text-muted small">Fetching invitation details…</div>
        </div>
      </div>

      <!-- No access -->
      <div class="col-md-8" v-else-if="eventAcess === 'no'">
        <div class="card">
          <div class="card-body text-center py-4">
            <i class="fas fa-user fa-3x mb-2"></i>
            <p>You are not host for viewing this invitation</p>
          </div>
        </div>
      </div>

      <!-- Pending access -->
      <div class="col-md-8" v-else>
        <div class="card skeleton-card">
          <div class="card-body">
            <div class="skeleton-line skeleton-line--lg mb-3"></div>
            <div class="skeleton-line skeleton-line--md mb-3"></div>
            <div class="skeleton-line skeleton-line--sm mb-4"></div>
            <div class="skeleton-block mb-3"></div>
            <div class="skeleton-line skeleton-line--sm mb-2"></div>
            <div class="skeleton-line skeleton-line--md"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Share Via Modal -->
    <ShareViaModal
      :visible="showShareVia"
      :title="eventTitle"
      :image="shareImage"
      :description="description"
      :dates="shareDates"
      :host="hostName"
      :location="eventPlace"
      :address="shareAddress"
      :map-url="mapHref"
      :url="shareUrl"
      @close="showShareVia = false"
    />

    <!-- QR Share Modal -->
    <QrShareModal
      :visible="showQrModal"
      :qrSrc="qrcodeImage"
      :url="webqrimage"
      @close="showQrModal = false"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { useRoute } from 'vue-router'
import api from '@/services/api'
import { useSessionStore } from '@/stores/session'
import ShareViaModal from '@/components/common/ShareViaModal.vue'
import QrShareModal from '@/components/common/QrShareModal.vue'
import GuestsList from '@/components/guests/GuestsList.vue'
import GuestsNotesList from '@/components/guests/GuestsNotesList.vue'
import { formatEventDateRange } from '@/utils/date'
import BackButton from '@/components/common/BackButton.vue'

export default {
  name: 'InvitationDetail',
  components: { ShareViaModal, QrShareModal, GuestsList, GuestsNotesList, BackButton },
  setup () {
    const route = useRoute()
    const session = useSessionStore()
    const userId = computed(() => session?.userId || '')

    // --- Event ID derivation ---
    const deriveEventId = (r) => {
      const cand = [
        r.params?.eventid, r.params?.id, r.params?.eventId,
        r.query?.eventid, r.query?.id, r.query?.eventId
      ].find(v => v != null && String(v).trim() !== '')
      if (cand) return String(cand)
      const lastSeg = (r.path || r.fullPath || '').split('?')[0].split('/').filter(Boolean).pop()
      return lastSeg && /^\d+$/.test(lastSeg) ? lastSeg : null
    }

    const eventId = ref(deriveEventId(route))
    const loaded = ref(false)
    const raw = ref(null)
    const members = ref([])
    const guestNotes = ref([])
    const guestNotesLoading = ref(false)
    const guestNotesLoaded = ref(false)
    const activeGuestsTab = ref('guests')

    // Share modals
    const showShareVia = ref(false)
    const showQrModal = ref(false)

    // Carousel
    const currentSlide = ref(0)
    const autoTimer = ref(null)
    const AUTOPLAY_MS = 4000
    const naturalW = ref(0)
    const naturalH = ref(0)
    const RATIO_169 = 16 / 9
    function onImgLoad (e) {
      const img = e.target
      naturalW.value = img.naturalWidth || img.width || 0
      naturalH.value = img.naturalHeight || img.height || 0
    }
    const useContain = computed(() => {
      const w = naturalW.value; const h = naturalH.value
      if (!w || !h) return true
      return (w / h) < RATIO_169
    })
    const nextSlide = () => {
      const n = slidesClean.value.length
      if (!n) return
      currentSlide.value = (currentSlide.value + 1) % n
    }
    const prevSlide = () => {
      const n = slidesClean.value.length
      if (!n) return
      currentSlide.value = (currentSlide.value - 1 + n) % n
    }
    const goToSlide = (i) => {
      const n = slidesClean.value.length
      if (!n) return
      currentSlide.value = Math.max(0, Math.min(i, n - 1))
    }
    const startAuto = () => {
      stopAuto()
      if (slidesClean.value.length > 1) autoTimer.value = setInterval(nextSlide, AUTOPLAY_MS)
    }
    const stopAuto = () => {
      if (autoTimer.value) { clearInterval(autoTimer.value); autoTimer.value = null }
    }
    const pauseAuto = stopAuto
    const resumeAuto = () => { if (slidesClean.value.length > 1) startAuto() }

    // Access/edit flags
    const cohostState = ref(null)
    const eventAccessState = ref(null)
    const eventEditState = ref(null)
    const eventDeleteState = ref(null)

    const cohostAddStatus = computed(() => (cohostState.value?.cohost_add_status ?? '0').toString())
    const eventAcess = computed(() => {
      const access = eventAccessState.value?.event_access
      if (access === undefined || access === null || String(access).trim() === '') return 'pending'
      return String(access).toLowerCase()
    })
    const eventEdit = computed(() => (eventEditState.value?.edit_option ?? 'no').toString())
    const eventDelete = computed(() => (eventDeleteState.value?.delete_option ?? 'no').toString())

    // Fetch
    async function loadInvitation () {
      try {
        loaded.value = false
        eventAccessState.value = { event_access: null }
        guestNotes.value = []
        guestNotesLoaded.value = false
        guestNotesLoading.value = false
        activeGuestsTab.value = 'guests'
        if (!eventId.value) { raw.value = null; members.value = []; return }

        const fd = new FormData()
        fd.append('userid', userId.value)
        fd.append('eventid', eventId.value)

        const res = await api.getInvitationDetail(fd)
        const payload = res?.data ?? res
        const ok = payload?.status === 'ok' || payload?.status === true
        if (!ok) { raw.value = null; members.value = []; return }

        raw.value = payload

        const guestsArray = Array.isArray(payload.data)
          ? payload.data
          : Array.isArray(payload?.data?.data)
            ? payload.data.data
            : []
        members.value = guestsArray

        cohostState.value = { cohost_add_status: payload?.cohost_add_status ?? payload?.data?.cohost_add_status ?? '0' }
        eventAccessState.value = { event_access: payload?.event_access ?? payload?.data?.event_access ?? 'no' }
        eventEditState.value = { edit_option: payload?.edit_option ?? payload?.data?.edit_option ?? 'no' }
        eventDeleteState.value = { delete_option: payload?.delete_option ?? payload?.data?.delete_option ?? 'no' }

        setDefaultGuestsTab()
        if (isNotesTabKey(activeGuestsTab.value)) ensureGuestNotesLoaded()

        // reset slider
        currentSlide.value = 0
        stopAuto()
        naturalW.value = 0
        naturalH.value = 0
        if (slidesClean.value.length > 1) startAuto()
      } catch (e) {
        console.error('Error fetching invitation detail:', e)
        raw.value = null
        members.value = []
      } finally {
        loaded.value = true
      }
    }

    const labelsGuestsRaw = computed(() => {
      const src = raw.value?.labels_guests ?? raw.value?.data?.labels_guests ?? []
      if (Array.isArray(src)) return src
      if (typeof src === 'string') return src.split(',').map(s => s.trim()).filter(Boolean)
      return []
    })

    const guestTabs = computed(() => labelsGuestsRaw.value.map((item, idx) => {
      const label = typeof item === 'string'
        ? item
        : item?.label ?? item?.name ?? item?.title ?? item?.text ?? ''
      const cleanedLabel = (label || '').trim() || `Tab ${idx + 1}`
      const keySource = item?.type ?? item?.key ?? item?.id ?? cleanedLabel
      const normalizedKey = String(keySource || `tab-${idx}`).trim().toLowerCase().replace(/\s+/g, '_') || `tab-${idx}`
      return { key: normalizedKey, label: cleanedLabel }
    }))

    const isNotesTabKey = (key) => {
      const k = String(key || '').toLowerCase()
      return k === 'notes' || k.includes('note')
    }

    const setDefaultGuestsTab = () => {
      const fallback = guestTabs.value[0]?.key || 'guests'
      activeGuestsTab.value = fallback
    }

    watch(guestTabs, (tabs) => {
      const fallback = tabs[0]?.key || 'guests'
      if (!tabs.find(t => t.key === activeGuestsTab.value)) activeGuestsTab.value = fallback
    })

    const setActiveGuestsTab = (key) => {
      activeGuestsTab.value = key
      if (isNotesTabKey(key)) ensureGuestNotesLoaded()
    }

    const ensureGuestNotesLoaded = async () => {
      if (!eventId.value || guestNotesLoading.value || guestNotesLoaded.value) return
      await loadGuestNotes()
    }

    async function loadGuestNotes () {
      if (!eventId.value) { guestNotes.value = []; guestNotesLoaded.value = false; return }
      try {
        guestNotesLoading.value = true
        const fd = new FormData()
        fd.append('event_id', eventId.value)
        const res = await api.getNotesMembersEventDetail(fd)
        const payload = res?.data ?? res
        const ok = payload?.status === 'ok' || payload?.status === true
        guestNotes.value = ok && Array.isArray(payload?.data) ? payload.data : []
        guestNotesLoaded.value = true
      } catch (e) {
        console.error('Error fetching guest notes:', e)
        guestNotes.value = []
        guestNotesLoaded.value = false
      } finally {
        guestNotesLoading.value = false
      }
    }

    watch(activeGuestsTab, (tab) => {
      if (isNotesTabKey(tab)) ensureGuestNotesLoaded()
    })

    const isGuestsNotesTab = computed(() => isNotesTabKey(activeGuestsTab.value))

    // Derived info
    const info = computed(() => raw.value?.eventsinfo?.[0] || {})
    const eventTitle = computed(() => info.value.eventname || info.value.orgname || '')
    const eventType = computed(() => info.value.event_type || 'new')
    const hostName = computed(() => info.value.hostname || raw.value?.host_name || '')
    const qrcodeImage = computed(() => info.value.qrcode_image || raw.value?.qrcode_image || '')
    const shareUrl = computed(() => info.value.share_url || '')

    const slidesRaw = computed(() => {
      const arr = []
      const pushIf = (v) => { if (v && String(v).trim() !== '') arr.push(String(v).trim()) }
      if (Array.isArray(info.value.photos)) info.value.photos.forEach(pushIf)
      if (arr.length === 0) pushIf(info.value.type_image)
      return arr
    })
    const slidesClean = computed(() => {
      const out = []; const seen = new Set()
      for (const s of slidesRaw.value) {
        const u = String(s).trim(); if (!u) continue
        if (seen.has(u)) continue
        seen.add(u); out.push(u)
      }
      return out
    })
    watch(slidesClean, (arr) => {
      if (!arr.length) { currentSlide.value = 0; stopAuto(); return }
      if (currentSlide.value >= arr.length) currentSlide.value = 0
      if (arr.length > 1 && !autoTimer.value) startAuto()
    })

    const fromDate = computed(() => info.value.fromdate || '')
    const toDate = computed(() => info.value.todate || '')
    const fromTime = computed(() => (info.value.fromtime || '').trim())
    const toTime = computed(() => (info.value.totime || '').trim())
    const eventPlace = computed(() => info.value.event_place || '')
    const Address = computed(() => info.value.address || '')
    const doorNo = computed(() => info.value.door_no || '')
    const lat = computed(() => info.value.lat || '')
    const lng = computed(() => info.value.lng || '')
    const description = computed(() => raw.value?.event_description || info.value.description || '')
    const rsvpOption = computed(() => Number(raw.value?.rsvp_option ?? info.value.rsvp_option ?? 0) === 1 ? 1 : 0)
    const rsvpFamily = computed(() => Number(raw.value?.rsvp_family ?? info.value.rsvp_family ?? 0) === 1 ? 1 : 0)
    const webqrimage = computed(() => info.value.webqrimage || '')
    const shareAddress = computed(() => {
      const parts = [eventPlace.value, doorNo.value, Address.value].filter(Boolean)
      return parts.join(', ')
    })

    const totals = computed(() => ({
      total: Number(raw.value?.total_members_count || 0),
      sent: Number(raw.value?.sent_members_count || 0),
      pending: Number(raw.value?.notsent_members_count || 0)
    }))

    const rsvp = computed(() => ({
      yes: Number(raw.value?.yescount || 0),
      no: Number(raw.value?.nocount || 0),
      maybe: Number(raw.value?.maybecount || 0),
      noresponse: Number(raw.value?.noresponsecount || 0)
    }))

    const attendance = computed(() => ({
      yesAdults: Number(raw.value?.yesadult || 0),
      yesChildren: Number(raw.value?.yeschild || 0),
      maybeAdults: Number(raw.value?.mayadult || 0),
      maybeChildren: Number(raw.value?.maychild || 0)
    }))

    const eventSendEnquiryCount = computed(() => Number(raw.value?.eventSendEnquiryCount || 0))

    const mapHref = computed(() => {
      if (!lat.value || !lng.value) return '#'
      return `https://www.google.com/maps?q=${encodeURIComponent(lat.value)},${encodeURIComponent(lng.value)}`
    })
    const hasMap = computed(() => Boolean(lat.value && lng.value))
    const mapEmbedSrc = computed(() => {
      if (!hasMap.value) return ''
      const query = `${encodeURIComponent(lat.value)},${encodeURIComponent(lng.value)}`
      return `https://www.google.com/maps?q=${query}&hl=en&output=embed`
    })

    const shareDates = computed(() => formatEventDateRange({
      fromDate: fromDate.value,
      toDate: toDate.value,
      fromTime: fromTime.value,
      toTime: toTime.value
    }))
    const eventDateRange = computed(() => shareDates.value || '')
    const shareImage = computed(() => slidesClean.value[0] || '')

    // Watch route changes (navigate between invitations)
    watch(() => route.fullPath, async () => {
      const next = deriveEventId(route)
      if (next !== eventId.value) {
        eventId.value = next
        await loadInvitation()
      }
    })

    onMounted(() => {
      loadInvitation()
      if (slidesClean.value.length > 1) startAuto()
    })
    onBeforeUnmount(() => { stopAuto() })

    return {
      // route-derived
      eventId,
      userId,

      // state
      loaded,
      members,
      guestTabs,
      activeGuestsTab,
      setActiveGuestsTab,
      guestNotes,
      guestNotesLoading,
      isGuestsNotesTab,
      loadInvitation,

      // info
      eventTitle, eventType, qrcodeImage, hostName,
      fromDate, toDate, fromTime, toTime,
      eventPlace, Address, doorNo, lat, lng, mapHref,
      description, webqrimage, totals, rsvp, attendance, eventSendEnquiryCount, hasMap, mapEmbedSrc, rsvpOption, rsvpFamily,
      shareAddress,

      // formatting
      // carousel
      slidesClean, currentSlide, nextSlide, prevSlide, goToSlide, pauseAuto, resumeAuto, onImgLoad, useContain,

      // flags
      cohostAddStatus, eventAcess, eventEdit, eventDelete,

      // share modals
      showShareVia, showQrModal, shareDates, shareImage, shareUrl, eventDateRange
    }
  }
}
</script>

<style>
/* Minimal modal css & helpers (no custom modals here, kept base styles for consistency) */
.gap-2 { gap: .5rem; }

:root{
  --brand-blue:#1da1f2;
  --brand-amber:#f5c542;
  --brand-purple:#6f42c1;
  --brand-green:#28a745;
  --brand-orange:#f39c12;
  --brand-red:#dc3545;
  --brand-primary:#0069d9;
}

/* Header */
.page-bar{ position: sticky; top: 0; z-index: 1000; background: #fff; border-bottom: 1px solid rgba(0,0,0,.06); }
.page-title{ font-weight: 600; font-size: 1.05rem; }

/* HERO / SLIDER */
.slide-frame{ position: relative; width: 100%; overflow: hidden; }
.slide-frame::before{ content: ""; display: block; padding-top: 56.25%; }
.slide-bg-blur{ position: absolute; inset: 0; background-size: cover; background-position: center; filter: blur(20px) brightness(0.9); transform: scale(1.1); }
.slide-img{ position: absolute; inset: 0; width: 100%; height: 100%; border-radius: .5rem; box-shadow: 0 2px 10px rgba(0,0,0,.08); }
.slide-img.fit-contain{ object-fit: contain; background: transparent; }
.slide-img.fit-cover{ object-fit: cover; object-position: top center; }

.nav{
  position: absolute; top: 50%; transform: translateY(-50%);
  border: 0; background: rgba(0,0,0,.35); color: #fff;
  width: 36px; height: 36px; border-radius: 50%;
  display:flex;align-items:center;justify-content:center; cursor: pointer;
}
.nav.prev{ left: 10px; } .nav.next{ right: 10px; }
.nav i{ font-size: 14px; }

.thumbs{ display: flex; flex-wrap: nowrap; overflow-x: auto; gap: .5rem; padding-bottom: .25rem; }
.thumb-btn{ padding: 0; border: 2px solid transparent; background: transparent; border-radius: .35rem; line-height: 0; flex: 0 0 auto; }
.thumb-btn.active{ border-color: var(--brand-primary); }
.thumb{ width: 88px; height: 56px; object-fit: cover; border-radius: .35rem; display: block; box-shadow: 0 1px 6px rgba(0,0,0,.08); }

/* Section titles */
.section-title{ font-weight: 600; font-size: 1rem; margin-bottom: .5rem; }

/* Icon rows */
.icon-row i{ color: var(--brand-primary); width: 22px; text-align: center; }
.emoji-icon{ display:inline-block; line-height:1; }

/* Count bars */
.count-bar{ display:flex; flex-wrap:wrap; }
.count-pill{
  flex:1 1 150px; min-width: 140px; background:#f8f9fa; border-radius:.5rem; padding:.8rem 1rem; color:#fff; margin:.25rem; text-align:center; position:relative;
  overflow:hidden; box-shadow:0 1px 6px rgba(0,0,0,.06);
}
.count-pill .num{font-weight:700;font-size:1.1rem;}
.count-pill .lbl{font-size:.85rem;opacity:.9;}
.count-total{background:var(--brand-blue);}
.count-sent{background:var(--brand-amber); color:#222;}
.count-pending{background:var(--brand-purple);}

/* RSVP status segmented bar */
.rsvp-bar{display:flex;flex-wrap:wrap;}
.rsvp-seg{
  flex:1 1 180px; min-width:160px; border-radius:.5rem; color:#fff;
  padding:.7rem 1rem; margin:.25rem; text-align:center; box-shadow:0 1px 6px rgba(0,0,0,.06);
}
.rsvp-yes{background:var(--brand-green);}
.rsvp-maybe{background:var(--brand-orange);}
.rsvp-no{background:var(--brand-red);}
.rsvp-nr{background:var(--brand-primary);}
.rsvp-seg .num{font-weight:700;margin-right:.35rem;}

/* Attendance cards */
.att-card{ border-radius:.5rem; box-shadow:0 1px 10px rgba(0,0,0,.07); overflow:hidden; }
.att-head{ background:#f3f3f3; color:#ff7a00; font-weight:700; font-size:.95rem; padding:.6rem .9rem; position:relative; }
.att-body{padding:.9rem;}
.att-box{ background:#f8f9fa; border-radius:.35rem; padding:.6rem .9rem; text-align:center; margin-bottom:.5rem; }
.att-box .h3{ font-weight:700; margin:0; }
.att-head.confirmed{color:#ff7a00;}
.att-head.maybe{color:#ff7a00;}

/* Vendors pills */
.vendor-pill{
  background:#eef5ff;border:1px solid #cfe2ff;color:#1b5ad7;
  padding:.35rem .65rem;border-radius:.35rem;margin-right:.35rem;
  font-size:.85rem; font-weight:600;
}

/* Post to this event */
.post-card{
  border-radius:14px;
  padding:12px 14px;
  background:#f9fafb;
  border:1px solid #eef2f7;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
}
.post-card__title{font-weight:700;color:#0f172a;}
.post-card__sub{color:#6b7280;font-size:.9rem;}
.post-card__icon{color:#94a3b8;}

/* Small helpers */
.text-small{font-size:.9rem;}
.link-arrow{color:var(--brand-primary);font-weight:600;}
.link-arrow i{font-size:.9rem;}

/* Badge tabs */
.badge-tabs{display:flex;flex-wrap:wrap;gap:.5rem;}
.badge-tab{
  border:1px solid #dbe2ef;
  background:#f8fafc;
  border-radius:999px;
  padding:.35rem .85rem;
  font-weight:600;
  color:#0f172a;
  transition:all .15s ease;
}
.badge-tab:hover{background:#eef4ff;}
.badge-tab.active{
  background:var(--brand-primary);
  color:#fff;
  border-color:var(--brand-primary);
  box-shadow:0 6px 16px rgba(0,105,217,.25);
}
.badge-tab:focus-visible{outline:2px solid #0d6efd;outline-offset:2px;}

/* Action menu */
.action-menu-toggle{
  border-radius: 10px;
  background: #f2f5fb;
  color: #0f172a;
  border: 1px solid #e3e8f0;
  transition: all .15s ease;
}
.action-menu-toggle:hover{ background:#e7eef9; color:#0b1324; }
.action-menu-toggle:focus-visible{ outline:2px solid #0d6efd; outline-offset:2px; }
.action-menu{
  border-radius: 12px;
  padding: .4rem;
  min-width: 12rem;
  border: 1px solid #e8ecf4;
  box-shadow: 0 12px 30px rgba(15,23,42,.14);
}
.action-menu__item{
  border-radius: 8px;
  padding: .6rem .75rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: .65rem;
  color: #111827;
}
.action-menu__item i{ color:#64748b; width:1.1rem; text-align:center; }
.action-menu__item:hover,
.action-menu__item:focus{
  background:#f3f6fb;
  color:#0f172a;
}
@media (max-width: 575.98px){ .page-title{font-size:1rem;} }

/* Skeleton loader */
.skeleton-card{ border-radius:.5rem; box-shadow:0 1px 10px rgba(0,0,0,.07); }
.skeleton-line,
.skeleton-block{
  position:relative; overflow:hidden; background:#e9ecef;
  border-radius:.35rem;
}
.skeleton-line{ height:14px; }
.skeleton-line--lg{ height:22px; width:70%; }
.skeleton-line--md{ width:55%; }
.skeleton-line--sm{ width:40%; }
.skeleton-block{ height:140px; border-radius:.5rem; }
.skeleton-line::after,
.skeleton-block::after{
  content:""; position:absolute; inset:0;
  background:linear-gradient(90deg, transparent, rgba(255,255,255,.6), transparent);
  transform:translateX(-100%);
  animation:skeleton-glow 1.5s ease-in-out infinite;
}
@keyframes skeleton-glow{
  0%{ transform:translateX(-100%); }
  100%{ transform:translateX(100%); }
}

.map-card{
  border:1px solid #e5e7eb;
  border-radius:12px;
  overflow:hidden;
  box-shadow:0 8px 20px rgba(15,23,42,.08);
  background:#fff;
}
.map-card__header{
  padding:.75rem 1rem;
  font-weight:600;
  color:#0f172a;
  border-bottom:1px solid #f3f4f6;
  font-size:.95rem;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:.5rem;
}
.map-card__header a{
  font-size:.85rem;
  color:var(--brand-primary);
  font-weight:500;
}
.map-frame{
  position:relative;
  padding-top:56.25%;
  background:#f1f5f9;
}
.map-frame__iframe{
  position:absolute;
  inset:0;
  width:100%;
  height:100%;
  border:0;
}
</style>
